import { useEffect, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { useParams, useSearchParams, Navigate } from 'react-router-dom'

import { Page, Text } from '@loadsmart/miranda-react'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import { useInitApplication } from 'core/ui/hooks/useInitApplication'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import Report from 'managed-analytics/ui/components/report/Report'
import { getPBIReportQueryOptions } from 'managed-analytics/use-cases/getPBIReports'
import { getPBITokenQueryOptions } from 'managed-analytics/use-cases/getPBIToken'

export default function ReportPage() {
  const { reportId } = useParams()

  const [searchParams] = useSearchParams()

  const rlsFilter = searchParams.get('rls_filter')
  const customerType = searchParams.get('customer_type')
  const roleName = searchParams.get('role_name')
  const profile = searchParams.get('profile')

  if (!rlsFilter || !customerType || !roleName || !profile) {
    return <Navigate to="/demo" />
  }

  return (
    <ReportPageContent
      reportId={reportId!}
      rlsFilter={rlsFilter}
      customerType={customerType}
      roleName={roleName}
      profile={profile}
    />
  )
}

type ReportPageContentProps = {
  reportId: string
  rlsFilter: string
  customerType: string
  roleName: string
  profile: string
}

function ReportPageContent({
  reportId,
  rlsFilter,
  customerType,
  roleName,
  profile,
}: ReportPageContentProps) {
  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const reportQuery = useSuspenseQuery(getPBIReportQueryOptions(reportId!))
  const tokenQuery = useSuspenseQuery(
    getPBITokenQueryOptions({ ...reportQuery.data, rlsFilter, customerType, roleName, profile })
  )

  const { track } = useAnalytics()
  
  // Initialize analytics to ensure Mixpanel is ready before tracking events
  useInitApplication()

  useEffect(() => {
    track(AnalyticsEvent.OpenReport, {
      'Report Name': reportQuery.data.reportName,
      'Dataset Id': reportQuery.data.datasetId,
      'Dataset Group Id': reportQuery.data.groupId,
      'PBI User Id': rlsFilter,
      'PBI User Type': customerType,
      'Role Name': roleName,
      'Default Report': false,
      Demo: false,
    })
  }, [track, reportQuery.data, rlsFilter, customerType, roleName, profile])

  return (
    <AppLayout>
      <Header
        title={
          <Text color="color-text-primary" variant="body-md-bold">
            {reportQuery.data.reportName}
          </Text>
        }
      />
      <Page.Content>
        <Page.Body>
          <Report
            selectedReport={{ ...reportQuery.data, rlsFilter, customerType, roleName }}
            accessToken={tokenQuery.data.embedToken}
            isReportLoading={false}
            isTokenLoading={false}
            isDefaultReport={false}
          />
        </Page.Body>
      </Page.Content>

      <ZendeskDialog
        requestType={RequestType.NewRequest}
        open={isZendeskDialogOpen}
        onClose={() => setIsZendeskDialogOpen(false)}
      />
    </AppLayout>
  )
}
